/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { GenericFinderDialogComponent, GenericFinderDialogConfig } from '@gh/builder-shared';
import { HousePlanRestService, ReportSettingsRestService } from '@gh/builder-shared-data';
import { createDialogRoute, InitViewService } from '@gh/common-shared';
import { AutocompleteItem, ID } from '@gh/common-shared-data';
import { FilteredDataQuery, getSlicedData } from '@gh/core-data';
import { TranslationService } from '@gh/core-mls';
import { DialogRouteSnapshot } from '@gh/core-ui';
import { GridLayoutService } from '@gh/core-ui-grid';
import { map$ } from '@gh/rx/operators';
import { Observable } from 'rxjs';
import {
  getReportRohcOfficeCcId,
  getReportRohcOfficeId,
  ReportRohcDetails,
  reportRohcDetailsFromDto,
  reportRohcDetailsToDto,
} from './report-roch-details.model';

export type ReportRohcOfficeFinderDialogConfig = GenericFinderDialogConfig;

export type ReportRohcOfficeCcFinderDialogConfig = GenericFinderDialogConfig;

@Injectable()
export class ReportRohcDetailsBusinessService implements InitViewService {
  constructor(private translations: TranslationService,
              private reportSettingsRest: ReportSettingsRestService,
              private housePlanRest: HousePlanRestService,
              private gridLayouts: GridLayoutService) {
  }

  initView(): any {
    return {
      details: this.get(),
      officeGridLayout: this.gridLayouts.get('officesReportsSettings'),
      officeCcGridLayout: this.gridLayouts.get('officeCCReportsSettings'),
    };
  }

  initOfficeFinderDialogView(route: DialogRouteSnapshot<ReportRohcOfficeFinderDialogConfig>): any {
    return {
      getId: getReportRohcOfficeId,
      defaultTitle: this.translations.get('lbl_find_offices'),
      dataSourceFactory: (filter: string) =>
        this.reportSettingsRest.findAllOfficesAsDataSource({ searchString: filter }),
      columnDefs: [
        { field: 'name', headerName: this.translations.get('lbl_office_name'), width: 250 },
        { field: 'masterZoneCode', headerName: this.translations.get('lbl_master_zone'), width: 250 },
      ],
    };
  }

  initOfficeCcFinderDialogView(route: DialogRouteSnapshot<ReportRohcOfficeCcFinderDialogConfig>): any {
    return {
      getId: getReportRohcOfficeCcId,
      defaultTitle: this.translations.get('lbl_find_cost_centers'),
      dataSourceFactory: (filter: string) =>
        this.reportSettingsRest.findAllOfficeCostCentersAsDataSource({
          searchString: filter,
          housePlanIds: route.input.filter.housePlanIds,
        }),
      columnDefs: [
        { field: 'code', headerName: this.translations.get('lbl_number'), width: 100 },
        { field: 'name', headerName: this.translations.get('lbl_name'), width: 200 },
        { field: 'officeName', headerName: this.translations.get('lbl_office_name'), width: 200 },
        {
          field: 'housePlanCode',
          headerName: this.translations.get('lbl_house_plan_estimate'),
          width: 170,
        },
      ],
    };
  }

  get(): Observable<ReportRohcDetails> {
    return this.reportSettingsRest.getReporRohc().pipe(map$(reportRohcDetailsFromDto));
  }

  update(details: ReportRohcDetails): Observable<ReportRohcDetails> {
    return this.reportSettingsRest.updateReporRohc(reportRohcDetailsToDto(details)).pipe(map$(reportRohcDetailsFromDto));
  }

  findAllHousePlansByQuery(sourceOfficeId: ID, query: FilteredDataQuery<string>): Observable<AutocompleteItem[]> {
    return this.housePlanRest.findAllAsAutocompleteDataSource({
        sourceOfficeId,
        searchString: query.filter,
      })
      .select(query).pipe(
        map$(getSlicedData));
  }
}

export const reportRohcOfficeFinderDialogRoute = createDialogRoute({
  component: GenericFinderDialogComponent,
  init: {
    service: ReportRohcDetailsBusinessService,
    selector: 'initOfficeFinderDialogView',
  },
});

export const reportRohcOfficeCcFinderDialogRoute = createDialogRoute({
  component: GenericFinderDialogComponent,
  init: {
    service: ReportRohcDetailsBusinessService,
    selector: 'initOfficeCcFinderDialogView',
  },
});
